import Component from "@rankers/lib/component";
import iconStar from "./assets/icon__star.svg";

/**
 * Configuration object for the RatingsGraph component.
 */
export const RatingsGraphConfig = {
  // Component identification
  componentTypeKey: "RatingsGraph",
  baseHtmlClass: "ratings_graph",
  selector: ".ratings_graph",

  // Data attribute keys for configuration
  datasetConfigKey: "ratings_graph__config",
  dataDatasetConfigKey: "data",
  showCountsDatasetConfigKey: "show_counts",
  showPercentagesDatasetConfigKey: "show_percentages",
  barColorDatasetConfigKey: "bar_color",
  maxBarWidthDatasetConfigKey: "max_bar_width",
  showStarsDatasetConfigKey: "show_stars",

  // CSS classes for component structure
  starsClass: "ratings_graph__stars",
  labelClass: "ratings_graph__label",
  barContainerClass: "ratings_graph__bar_container",
  barClass: "ratings_graph__bar",
  textClass: "ratings_graph__text",

  // Default configuration values
  showCounts: true,
  showPercentages: false,
  showStars: false,
  barColor: "hotpink", // Default yellow/gold color
  maxBarWidth: 100, // Maximum width percentage for bars
};

/**
 * RatingsGraph component for displaying rating distribution.
 * 
 * This component renders a horizontal bar chart showing the distribution
 * of ratings with star icons, proportional bars, and count/percentage text.
 * 
 * @extends Component
 */
export class RatingsGraph extends Component {
  /**
   * Returns the configuration object for this component.
   * @returns {Object} The RatingsGraphConfig object
   */
  static get config() {
    return RatingsGraphConfig;
  }

  /**
   * Creates a new RatingsGraph instance.
   * 
   * @param {string|HTMLElement} id_or_element - Either an element ID string or a direct reference to the DOM element
   */
  constructor(id_or_element) {
    super({ element: id_or_element, config: RatingsGraph.config });

    this.setDatasetConfig(RatingsGraph.config.datasetConfigKey);
    
    // Parse configuration from dataset
    this._data = this.getDatasetConfigValue(RatingsGraph.config.dataDatasetConfigKey) || {};
    this._showCounts = this.getDatasetConfigValue(RatingsGraph.config.showCountsDatasetConfigKey) ?? RatingsGraph.config.showCounts;
    this._showPercentages = this.getDatasetConfigValue(RatingsGraph.config.showPercentagesDatasetConfigKey) ?? RatingsGraph.config.showPercentages;
    this._showStars = this.getDatasetConfigValue(RatingsGraph.config.showStarsDatasetConfigKey) ?? RatingsGraph.config.showStars;
    this._barColor = this.getDatasetConfigValue(RatingsGraph.config.barColorDatasetConfigKey) || RatingsGraph.config.barColor;
    this._maxBarWidth = this.getDatasetConfigValue(RatingsGraph.config.maxBarWidthDatasetConfigKey) || RatingsGraph.config.maxBarWidth;

    // Clear any existing content
    this.element.innerHTML = "";

    // Create rating elements and set up grid
    this.createRatingRows();

    // Set up intersection observer for animations
    this.setupIntersectionObserver();

    // Initial render
    this.render();
  }

  /**
   * Creates rating row elements based on the data.
   * @returns {Array} Array of row elements
   */
  createRatingRows() {
    const sortedRatings = this.getSortedRatings();
    const maxCount = this.getMaxCount();

    // Set up the main container as a grid and set CSS custom properties
    this.element.style.cssText = `display: grid; grid-template-columns: auto 1fr auto; --ratings-graph-bar-color: ${this._barColor};`;

    const allElements = [];
    sortedRatings.forEach(rating => {
      const ratingData = this._data[rating];
      if (!ratingData) return;

      const rowElements = this.createRatingRowElements(rating, ratingData, maxCount);
      rowElements.forEach(element => {
        this.element.appendChild(element);
        allElements.push(element);
      });
    });

    return allElements;
  }

  /**
   * Creates the elements for a single rating row.
   * @param {string} rating - The rating value (e.g., "5", "4", etc.)
   * @param {Object} data - The data for this rating (count and percent)
   * @param {number} maxCount - The maximum count across all ratings
   * @returns {Array} Array of elements for this row (label, bar, text)
   */
  createRatingRowElements(rating, data, maxCount) {
    const elements = [];

    // Create stars/label container (shows either full stars or label with star)
    const starsLabelContainer = this.createStarsLabelContainer(rating);
    elements.push(starsLabelContainer);

    // Create bar container and bar
    const barContainer = this.createBarContainer(data.count, maxCount);
    elements.push(barContainer);

    // Create text display
    const textDisplay = this.createTextDisplay(data);
    elements.push(textDisplay);

    return elements;
  }

  /**
   * Creates the stars/label container for a rating.
   * Shows either full stars (if show_stars is true) or label with single star (if show_stars is false).
   * @param {string} rating - The rating value
   * @returns {HTMLElement} The stars/label container element
   */
  createStarsLabelContainer(rating) {
    const container = document.createElement("div");

    if (this._showStars) {
      // Show full stars (e.g., ★★★★★ for rating 5)
      container.className = RatingsGraph.config.starsClass;
      const ratingNumber = parseInt(rating, 10);
      for (let i = 0; i < ratingNumber; i++) {
        const starWrapper = document.createElement("span");
        starWrapper.innerHTML = iconStar;
        container.appendChild(starWrapper);
      }
    } else {
      // Show label with single star (e.g., "5★")
      container.className = RatingsGraph.config.labelClass;
      
      // Add rating number
      const ratingText = document.createElement("span");
      ratingText.textContent = rating;
      container.appendChild(ratingText);
      
      // Add single star icon after the number
      const starWrapper = document.createElement("span");
      starWrapper.innerHTML = iconStar;
      container.appendChild(starWrapper);
    }

    return container;
  }

  /**
   * Creates the bar container and bar element.
   * @param {number} count - The count for this rating
   * @param {number} maxCount - The maximum count across all ratings
   * @returns {HTMLElement} The bar container element
   */
  createBarContainer(count, maxCount) {
    const container = document.createElement("div");
    container.className = RatingsGraph.config.barContainerClass;

    const bar = document.createElement("div");
    bar.className = RatingsGraph.config.barClass;
    const width = maxCount > 0 ? (count / maxCount) * this._maxBarWidth : 0;
    
    // Initially set width to 0 for animation, store target width as data attribute
    bar.style.cssText = `height: 100%; background-color: var(--ratings-graph-bar-color); width: 0%;`;
    bar.setAttribute('data-target-width', width);

    container.appendChild(bar);
    return container;
  }

  /**
   * Creates the text display for count and percentage.
   * @param {Object} data - The data containing count and percent
   * @returns {HTMLElement} The text display element
   */
  createTextDisplay(data) {
    const container = document.createElement("div");
    container.className = RatingsGraph.config.textClass;

    const parts = [];
    if (this._showCounts) {
      parts.push(data.count.toString());
    }
    if (this._showPercentages) {
      parts.push(`${data.percent}%`);
    }

    container.textContent = parts.join(" • ");
    return container;
  }

  /**
   * Gets sorted rating keys (highest to lowest).
   * @returns {Array} Sorted array of rating keys
   */
  getSortedRatings() {
    return Object.keys(this._data).sort((a, b) => parseInt(b, 10) - parseInt(a, 10));
  }

  /**
   * Gets the maximum count value from the data.
   * @returns {number} The maximum count
   */
  getMaxCount() {
    return Math.max(...Object.values(this._data).map(d => d.count || 0));
  }

  /**
   * Sets up intersection observer to trigger animations when component is in view.
   */
  setupIntersectionObserver() {
    const options = {
      threshold: 0.1,
      rootMargin: '0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting && !this.element.classList.contains('ratings_graph--viewed')) {
          // Add viewed class to trigger animations
          this.element.classList.add('ratings_graph--viewed');
          
          // Add staggered animation classes to each bar
          const bars = this.element.querySelectorAll(`.${RatingsGraph.config.barClass}`);
          bars.forEach((bar, index) => {
            setTimeout(() => {
              bar.classList.add('ratings_graph__bar--animate');
              // Set the actual width after adding animation class
              const targetWidth = bar.getAttribute('data-target-width');
              bar.style.width = `${targetWidth}%`;
            }, index * 100); // 100ms delay between each bar
          });
          
          // Stop observing after animation triggers
          observer.disconnect();
        }
      });
    }, options);

    observer.observe(this.element);
  }

  /**
   * Renders the component.
   * This method can be called to update the component's display.
   */
  render() {
    // Grid layout is set up in createRatingRows
    // This method is available for future enhancements
  }
}